"use client";

import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { 
  getTaskTypes, 
  getTasks, 
  claimTask, 
  submitLinkedInPost,
  Task, 
  TaskType 
} from "../../api/referralApi";
import TaskCard from "./TaskCard";
import LinkedInModal from "./LinkedInModal";

// Combined task data structure
interface CombinedTask {
  task: Task;
  taskType: TaskType;
}

interface SocialTasksSectionProps {
  onBalanceRefresh?: () => void;
}

const SocialTasksSection = ({ onBalanceRefresh }: SocialTasksSectionProps) => {
  const [combinedTasks, setCombinedTasks] = useState<CombinedTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchAllTaskData();
  }, []);

  const fetchAllTaskData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch both task types and user tasks in parallel
      const [taskTypes, tasksResponse] = await Promise.all([
        getTaskTypes(),
        getTasks()
      ]);
      
      // Combine task types with user task status
      // The taskId in tasks matches the identifier in taskTypes
      const combined: CombinedTask[] = taskTypes
        .filter(taskType => taskType.identifier !== 'producthunt_rate')
        .map(taskType => {
        const userTask = tasksResponse.tasks.find(
          t => t.taskId === taskType.identifier
        );
        
        // If user task doesn't exist, create a default NOT_STARTED task
        const task: Task = userTask || {
          taskId: taskType.identifier,
          status: "NOT_STARTED",
          minRewardValue: taskType.minRewardValue,
          maxRewardValue: taskType.maxRewardValue,
          rewardValue: 0,
          verificationDueAt: null
        };
        
        return {
          task,
          taskType
        };
      });
      
      setCombinedTasks(combined);
    } catch (error) {
      console.error("Error fetching task data:", error);
      toast.error("Failed to load tasks");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInitiateTask = async (task: Task) => {
    // For LinkedIn, show modal
    if (task.taskId === "linkedin_post") {
      setSelectedTask(task);
      setIsModalOpen(true);
      return;
    }
    
    // For other tasks, the URL is already opened in TaskCard
    // Call BOTH claim API and tasks API simultaneously
    // We need verificationDueAt from tasks API to start the timer
    try {
      // Call both APIs in parallel - ignore their responses regarding status
      await Promise.all([
        claimTask({ taskId: task.taskId }),
        new Promise(resolve => setTimeout(resolve, 500)) // Small delay to ensure backend processes
      ]);
      
      toast.info("Waiting for verification");
      
      // Fetch updated tasks to get verificationDueAt
      const tasksResponse = await getTasks();
      
      // Update combined tasks with new data
      setCombinedTasks(prevCombined => 
        prevCombined.map(combined => {
          const updatedTask = tasksResponse.tasks.find(
            t => t.taskId === combined.task.taskId
          );
          
          if (updatedTask) {
            // Use the verificationDueAt from API response
            
            return {
              ...combined,
              task: updatedTask
            };
          }
          
          return combined;
        })
      );
    } catch (error) {
      // console.error("Error initiating task:", error);
      toast.error("Failed to initiate task");
    }
  };

  const handleClaimReward = async (task: Task) => {
    try {
      const response = await claimTask({ 
        taskId: task.taskId
      });
      
      if (response.success) {
        toast.success(`🎉 ${response.coinsEarned} coins earned!`);
        
        // Refresh all task data
        await fetchAllTaskData();
      } else {
        toast.error(response.message || "Failed to claim reward");
      }
    } catch (error) {
      // console.error("Error claiming reward:", error);
      toast.error("Failed to claim reward");
    }
  };

  const handleLinkedInSubmit = async (postUrl: string, postContent: string): Promise<boolean> => {
    if (selectedTask) {
      try {
        // Call the LinkedIn submit API with URL and content
        const response = await submitLinkedInPost(postUrl, postContent);
        
        if (response.success) {
          
          // Refresh tasks to get updated status
          await fetchAllTaskData();
          
          return true;
        } else {
          toast.error(response.message || "Failed to submit LinkedIn post");
          return false;
        }
      } catch (error: any) {
        // console.error("Error submitting LinkedIn post:", error);
        const errorMessage = error?.response?.data?.message || "Failed to submit LinkedIn post. Please try again.";
        toast.error(errorMessage);
        return false;
      }
    }
    return false;
  };

  const handleModalClaimReward = async () => {
    if (selectedTask) {
       // This simulates the "Initiate Task" flow that happens for other tasks
       // It requests verification/claims the initial state
      try {
        await claimTask({ taskId: selectedTask.taskId });
        
        toast.success("Verification started!");
        
        // Refresh tasks to get updated status and verification timer
        await fetchAllTaskData();
        
        // Close modal
        handleModalClose();
      } catch (error) {
        // console.error("Error starting verification:", error);
        toast.error("Failed to start verification process");
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTask(null);
  };

  if (isLoading) {
    return (
      <div className="rounded-xl bg-white p-6 shadow-md md:p-8">
        <h2 className="mb-8 text-center text-3xl font-bold text-gray-900">
          Earn ResumeMate Coins
        </h2>
        
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm animate-pulse">
              {/* Header skeleton */}
              <div className="mb-4 flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-gray-200"></div>
                <div className="h-5 w-32 rounded bg-gray-200"></div>
              </div>

              {/* Reward skeleton */}
              <div className="mb-2">
                <div className="h-6 w-24 rounded bg-gray-200"></div>
              </div>

              {/* Button skeleton */}
              <div className="mt-4 h-12 w-full rounded-lg bg-gray-200"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-xl bg-white p-6 shadow-md md:p-8">
        <h2 className="mb-8 text-center text-3xl font-bold text-gray-900">
            Earn ResumeMate Coins
        </h2>
        
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {combinedTasks.map(({ task, taskType }) => (
            <TaskCard
              key={task.taskId}
              task={task}
              taskType={taskType}
              onInitiateTask={handleInitiateTask}
              onClaimReward={handleClaimReward}
              onBalanceRefresh={onBalanceRefresh}
            />
          ))}
        </div>
      </div>

      {isModalOpen && selectedTask && (
        <LinkedInModal
          task={selectedTask}
          onSubmit={handleLinkedInSubmit}
          onClaimReward={handleModalClaimReward}
          onClose={handleModalClose}
        />
      )}
    </>
  );
};

export default SocialTasksSection;